/**
 * FollowUpForm Component - Form for creating/editing follow-ups
 */

import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { ArrowBack, Save } from "@mui/icons-material";
import { useCreateFollowUp, useUpdateFollowUp } from "../hooks/useFollowUps.js";
import { useAuth } from "../../../contexts/AuthContext.jsx";
import EntitySearchAutocomplete from "./EntitySearchAutocomplete.jsx";
import EntitySuggestionCard from "./EntitySuggestionCard.jsx";
import { getEntityContext } from "../services/entityContextService.js";

/**
 * FollowUpForm Component
 * @param {Object} props
 * @param {Object} [props.initialData] - Initial follow-up data for editing
 * @param {Function} [props.onSuccess] - Success callback
 */
const FollowUpForm = ({ initialData = null, onSuccess }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const createMutation = useCreateFollowUp();
  const updateMutation = useUpdateFollowUp();

  // Get pre-fill data from URL params
  const entityTypeParam = searchParams.get("entityType");
  const entityIdParam = searchParams.get("entityId");

  // Create selectedEntity object from initialData if editing
  const getInitialSelectedEntity = () => {
    if (initialData?.entityType && initialData?.entityId) {
      return {
        entityType: initialData.entityType,
        entityId: initialData.entityId,
        displayName: initialData.entityContext?.name || initialData.entityId,
        subtitle: initialData.entityContext?.phone || "",
        metadata: initialData.entityContext || {},
      };
    }
    return null;
  };

  const [formData, setFormData] = useState({
    entityType: initialData?.entityType || entityTypeParam || "",
    entityId: initialData?.entityId || entityIdParam || "",
    selectedEntity: getInitialSelectedEntity(),
    title: initialData?.title || "",
    description: initialData?.description || "",
    scheduledDate: initialData?.scheduledDate
      ? new Date(initialData.scheduledDate).toISOString().split("T")[0]
      : "",
    dueDate: initialData?.dueDate
      ? new Date(initialData.dueDate).toISOString().split("T")[0]
      : "",
    priority: initialData?.priority || "medium",
    assignedTo: initialData?.assignedTo || user?.id || "",
    notes: initialData?.notes || "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Load entity context if entityType and entityId are provided
  useEffect(() => {
    const loadEntityContext = async () => {
      if (entityTypeParam && entityIdParam && !initialData) {
        try {
          // This would need to be implemented in the service
          // For now, we'll just set the entity type and ID
          setFormData((prev) => ({
            ...prev,
            entityType: entityTypeParam,
            entityId: entityIdParam,
          }));
        } catch (error) {
          console.error("Error loading entity context:", error);
        }
      }
    };

    loadEntityContext();
  }, [entityTypeParam, entityIdParam, initialData]);

  // Calculate default due date (3 days from now)
  useEffect(() => {
    if (!formData.dueDate && !initialData) {
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + 3);
      setFormData((prev) => ({
        ...prev,
        scheduledDate: new Date().toISOString().split("T")[0],
        dueDate: defaultDate.toISOString().split("T")[0],
      }));
    }
  }, [initialData]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleEntitySelect = (entity) => {
    if (entity) {
      setFormData((prev) => ({
        ...prev,
        selectedEntity: entity,
        entityType: entity.entityType,
        entityId: entity.entityId,
        title: prev.title || `Follow-up: ${entity.displayName}`,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        selectedEntity: null,
        entityType: "",
        entityId: "",
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    // Only validate entity selection when creating (not editing)
    if (!initialData && (!formData.entityType || !formData.entityId)) {
      newErrors.entity = "Please select an entity (quote, customer, invoice, order, or lead)";
    }

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.dueDate) {
      newErrors.dueDate = "Due date is required";
    }

    if (!formData.assignedTo) {
      newErrors.assignedTo = "Assigned to is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      const followUpData = {
        type: formData.entityType || "general",
        entityType: formData.entityType,
        entityId: formData.entityId,
        title: formData.title.trim(),
        description: formData.description.trim(),
        scheduledDate: formData.scheduledDate || formData.dueDate,
        dueDate: formData.dueDate,
        priority: formData.priority,
        assignedTo: formData.assignedTo,
        notes: formData.notes.trim(),
      };

      if (initialData) {
        // Update existing follow-up
        const result = await updateMutation.mutateAsync({
          followUpId: initialData.id,
          updateData: followUpData,
        });
        // Success - clear any previous errors
        setErrors({});
        if (onSuccess) {
          onSuccess(result);
        } else {
          navigate(`/followups/${initialData.id}`);
        }
      } else {
        // Create new follow-up
        const result = await createMutation.mutateAsync(followUpData);
        // Success - clear any previous errors
        setErrors({});
        if (onSuccess) {
          onSuccess(result);
        } else {
          navigate("/followups");
        }
      }
    } catch (error) {
      console.error("Error saving follow-up:", error);
      setErrors({ submit: error.message || "Failed to save follow-up" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/followups")}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h5">
          {initialData ? "Edit Follow-up" : "Create Follow-up"}
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {errors.submit && (
                <Grid item xs={12}>
                  <Alert severity="error">{errors.submit}</Alert>
                </Grid>
              )}

              {/* Entity Selection */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Entity Selection
                </Typography>
                {initialData ? (
                  // When editing, show read-only entity info
                  <Box>
                    <TextField
                      fullWidth
                      label="Entity"
                      value={`${formData.entityType} - ${formData.entityId}`}
                      disabled
                      helperText="Entity cannot be changed when editing"
                    />
                    {formData.selectedEntity && (
                      <Box sx={{ mt: 2 }}>
                        <EntitySuggestionCard entity={formData.selectedEntity} />
                      </Box>
                    )}
                  </Box>
                ) : (
                  // When creating, show search field
                  <>
                    <EntitySearchAutocomplete
                      value={formData.selectedEntity}
                      onChange={handleEntitySelect}
                      label="Search for Quote, Customer, Invoice, Order, or Lead"
                      disabled={!!entityTypeParam}
                    />
                    {errors.entity && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                        {errors.entity}
                      </Typography>
                    )}
                    {formData.selectedEntity && (
                      <Box sx={{ mt: 2 }}>
                        <EntitySuggestionCard entity={formData.selectedEntity} />
                      </Box>
                    )}
                  </>
                )}
              </Grid>

              {/* Title */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title *"
                  value={formData.title}
                  onChange={handleChange("title")}
                  error={!!errors.title}
                  helperText={errors.title}
                  required
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={handleChange("description")}
                  multiline
                  rows={3}
                />
              </Grid>

              {/* Dates */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Scheduled Date"
                  type="date"
                  value={formData.scheduledDate}
                  onChange={handleChange("scheduledDate")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Due Date *"
                  type="date"
                  value={formData.dueDate}
                  onChange={handleChange("dueDate")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={!!errors.dueDate}
                  helperText={errors.dueDate}
                  required
                />
              </Grid>

              {/* Priority */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={formData.priority}
                    onChange={handleChange("priority")}
                    label="Priority"
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Assigned To */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Assigned To *"
                  value={formData.assignedTo}
                  onChange={handleChange("assignedTo")}
                  error={!!errors.assignedTo}
                  helperText={errors.assignedTo || "User ID of the person assigned"}
                  required
                />
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={handleChange("notes")}
                  multiline
                  rows={4}
                />
              </Grid>

              {/* Submit Buttons */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/followups")}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                    disabled={loading}
                  >
                    {initialData ? "Update" : "Create"} Follow-up
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default FollowUpForm;

